// @flow
import { useMemo, useState } from 'react';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useHasPermission from '../../hooks/useHasPermission';
import VarianceReportService from '../../services/varianceReportService';
import { formatDateLocale } from '../../utils/date-helpers';
import { errorText, notesByAccount } from './utils';
import messages from './messages';
import type {
  DrillDown,
  GlDetail,
  Note,
  ReportRow,
  VarianceReport,
} from './types';

const service = new VarianceReportService();

const MONTH_FORMAT = 'YYYY-MM';

const savingKey = (
  scope: { propertyId: string, month: string },
  accountNo: string,
) => `${scope.propertyId}:${scope.month}:${accountNo}`;

type NoteSave = {
  scope: { organizationId: string, propertyId: string, month: string },
  accountNo: string,
  note: string,
};

const lastMonth = () =>
  formatDateLocale(moment().subtract(1, 'month').toDate(), MONTH_FORMAT);

export type VarianceReportViewModel = {
  title: string,
  subtitle: ?string,
  isPropertySelected: boolean,
  month: string,
  onMonthChange: (value: ?Object) => void,
  isLoading: boolean,
  error: ?string,
  rows: Array<ReportRow>,
  notes: { [accountNo: string]: Note },
  canEditNotes: boolean,
  onSaveNote: (accountNo: string, note: string) => void,
  /** Changes with the property and month, so note fields start fresh on a switch. */
  scopeKey: string,
  /** Accounts on this report whose note is being saved. */
  savingAccountNos: Array<string>,
  onDrillDown: (drillDown: DrillDown) => void,
  drillDown: ?DrillDown,
  onCloseDrillDown: () => void,
};

export const useVarianceReport = (): VarianceReportViewModel => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id;
  const enabled = Boolean(organizationId && propertyId);
  const canEditNotes = useHasPermission('variance-report-edit');

  const [month, setMonth] = useState(lastMonth);
  // The drill-down remembers the property it was opened on, so it is gone on the same render a
  // property switch happens, before the modal could fetch against the new property.
  const [openDrillDown, setOpenDrillDown] = useState<?{
    propertyId: ?string,
    drillDown: DrillDown,
  }>(null);
  const drillDown =
    openDrillDown && openDrillDown.propertyId === propertyId
      ? openDrillDown.drillDown
      : null;
  // Notes with a save in flight. Their fields are locked until it settles, so two saves of one
  // note never race and an edit made during a save is never overwritten by it.
  const [savingKeys, setSavingKeys] = useState<Array<string>>([]);
  const scopeKey = `${propertyId ?? ''}:${month}`;

  const queryKey = ['varianceReport', organizationId, propertyId, month];
  const reportQuery = useQuery<VarianceReport>(
    queryKey,
    () =>
      service.getVarianceReport(organizationId ?? '', propertyId ?? '', month),
    {
      enabled,
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      // An Intacct setup error will not fix itself, and each try is several Intacct calls.
      retry: false,
    },
  );

  // The scope travels with each save, so a save still in flight after a month or property
  // switch writes to the report it was made on, not the one now on screen.
  const saveNote = useMutation(
    ({ scope, accountNo, note }: NoteSave) =>
      service.updateVarianceReportNote(scope.organizationId, scope.propertyId, {
        month: scope.month,
        accountNo,
        note,
      }),
    {
      onMutate: ({ scope, accountNo }: NoteSave) => {
        setSavingKeys((keys) => [...keys, savingKey(scope, accountNo)]);
      },
      onSettled: (
        _saved: mixed,
        _error: mixed,
        { scope, accountNo }: NoteSave,
      ) => {
        const key = savingKey(scope, accountNo);
        setSavingKeys((keys) => keys.filter((k) => k !== key));
      },
      onSuccess: async (saved: ?Note, { scope, accountNo }: NoteSave) => {
        const key = [
          'varianceReport',
          scope.organizationId,
          scope.propertyId,
          scope.month,
        ];
        // A refetch already in flight may have read the notes before this save landed. Cancel it
        // first, or it would put the old note back when it returns.
        await queryClient.cancelQueries(key);
        queryClient.setQueryData(key, (cached: ?VarianceReport) =>
          cached
            ? {
                ...cached,
                notes: [
                  ...cached.notes.filter((n) => n.accountNo !== accountNo),
                  ...(saved ? [saved] : []),
                ],
              }
            : cached,
        );
      },
      onError: (error: mixed) => {
        toastr.error(
          intl.formatMessage(messages.noteSaveError),
          errorText(error) ?? '',
        );
      },
    },
  );

  const report = reportQuery.data;
  const notes = useMemo(() => notesByAccount(report?.notes), [report]);
  const rows = report?.rows ?? [];

  return {
    title: intl.formatMessage(messages.title),
    subtitle: report
      ? intl.formatMessage(messages.subtitle, {
          propertyName: report.propertyName,
          budgetId: report.budgetId,
        })
      : null,
    isPropertySelected: enabled,
    month,
    onMonthChange: (value) => {
      if (value && moment(value).isValid()) {
        setMonth(formatDateLocale(moment(value).toDate(), MONTH_FORMAT));
        setOpenDrillDown(null);
      }
    },
    isLoading: reportQuery.isLoading,
    error: reportQuery.isError
      ? errorText(reportQuery.error) ?? intl.formatMessage(messages.loadError)
      : null,
    rows,
    notes,
    canEditNotes,
    onSaveNote: (accountNo, note) =>
      saveNote.mutate({
        scope: {
          organizationId: organizationId ?? '',
          propertyId: propertyId ?? '',
          month,
        },
        accountNo,
        note,
      }),
    scopeKey,
    savingAccountNos: savingKeys
      .filter((key) => key.startsWith(`${scopeKey}:`))
      .map((key) => key.slice(scopeKey.length + 1)),
    onDrillDown: (next) => setOpenDrillDown({ propertyId, drillDown: next }),
    drillDown,
    onCloseDrillDown: () => setOpenDrillDown(null),
  };
};

export type GlDetailViewModel = {
  isLoading: boolean,
  error: ?string,
  detail: ?GlDetail,
};

export const useGlDetail = (
  month: string,
  drillDown: ?DrillDown,
): GlDetailViewModel => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId;
  const propertyId = selectedProperty?.id;

  const query = useQuery<GlDetail>(
    [
      'varianceReportGlDetail',
      organizationId,
      propertyId,
      month,
      drillDown?.accountNo,
      drillDown?.range,
    ],
    () =>
      service.getVarianceReportGlDetail(
        organizationId ?? '',
        propertyId ?? '',
        month,
        drillDown?.accountNo ?? '',
        drillDown?.range ?? 'month',
      ),
    {
      enabled: Boolean(organizationId && propertyId && drillDown),
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      retry: false,
    },
  );

  return {
    isLoading: query.isLoading,
    error: query.isError
      ? errorText(query.error) ?? intl.formatMessage(messages.glDetailError)
      : null,
    detail: query.data,
  };
};
